import { useEffect, useState } from 'preact/hooks';
import {
  getPlan,
  listAllRecordsByType,
  listAllRecordsWithLinks,
  planHas,
  searchPositionRoster,
  type PlanApi,
  type PositionSearchResult,
  type RecordRow,
} from '../lib/api';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { humanizeValue } from '../lib/fieldDisplay';
import {
  AA,
  LINK,
  creditEnds,
  creditsForPerson,
  positionOf,
  readsWithPerson,
  type CreditEnd,
} from '../lib/auditionSpread';
import './spread.css';

// W2 — PEOPLE: everybody a household has met doing this, and what each of
// them did (decision `9753e5ed`).
//
// ===================================================================
// WHAT THIS REPLACES, AND WHY REPLACING IT WAS THE WHOLE POINT
// ===================================================================
// The People tab was the generic `Registry` over `aa_person` — one flat list
// of names, each row opening the schema-driven editor. Which is to say: the
// screen showed a family exactly what they had typed in, and nothing they had
// not. A casting director met three times read the same as one met once; a
// 1st AD who has now been on two of their productions read as a name.
//
// Everything on this screen that is not the name is DERIVED — the position,
// the production, the read count. None of it is a field on `aa_person`, and
// that is deliberate: a `position` column would hold ONE title, so the same
// Key MUA on a second job would either overwrite the first or mint a second
// person (recordTypes.ts's aa_crew_credit header has the long form).
//
// ===================================================================
// THE COUNTS ARE THIS HOUSEHOLD'S, AND THERE IS NO OTHER KIND
// ===================================================================
// ⚠ Somebody will eventually want "this casting director across CallToWrap" —
// one profile, one reputation, a number spanning families. It is refused by
// ruling (`deeb6266`). Every number here comes from this tenant's own
// auditions and its own credits, read through the ordinary gated door; there
// is no cross-household figure available on this screen and there must never
// be one.
//
// ===================================================================
// 2026-09-19 — READ TENANT-WIDE, FULL STOP
// ===================================================================
// This screen used to read through lib/subjectScope.ts, now gone: an
// account is one career (Huan's ruling), so there is no household to fall
// back to and no performer to narrow into. Every read here is simply this
// tenant's own.

export interface HouseholdPeopleProps {
  tenantId: string;
  onOpen: (personId: string) => void;
}

interface Line {
  person: RecordRow;
  /** The most recently learned credit, or null. See creditEnds on why
   * "latest" can only mean "most recently learned" here. */
  latest: CreditEnd | null;
  /** The production the latest credit points at, when we can read it. */
  latestProject: RecordRow | null;
  credits: number;
  reads: number;
}

export function HouseholdPeople({ tenantId, onOpen }: HouseholdPeopleProps) {
  const [lines, setLines] = useState<Line[] | null>(null);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [plan, setPlan] = useState<PlanApi | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  // ===================================================================
  // P3 — THE POSITION SEARCH (people.position_roster, Pro): "who has 1st
  // AD'd for us?" across every production this household has ever filed a
  // credit for. Gated WHOLE server-side (actorPositionRoster.ts), so below
  // Pro this never fires a search at all — the box itself is not offered.
  // ===================================================================
  const [positionQuery, setPositionQuery] = useState('');
  const [positionResults, setPositionResults] = useState<PositionSearchResult[] | null>(null);
  const [positionSearching, setPositionSearching] = useState(false);
  const [positionSay, setPositionSay] = useState<string | null>(null);

  async function runPositionSearch(q: string) {
    const trimmed = q.trim();
    if (!trimmed) {
      setPositionResults(null);
      setPositionSay(null);
      return;
    }
    setPositionSearching(true);
    setPositionSay(null);
    try {
      const results = await searchPositionRoster(tenantId, trimmed);
      setPositionResults(results);
    } catch {
      setPositionResults(null);
      setPositionSay('That search could not be run just now.');
    } finally {
      setPositionSearching(false);
    }
  }

  // ⚠ A HINT, NEVER A GATE (lib/api's own header on planHas). The tier decides
  // one sentence of empty-state copy on this screen and nothing else — the
  // real refusal is server-side, where the pack's
  // `captureModelTypeFeatures` narrows what a capture may create. A failed
  // plan read leaves `plan` null and `planHas` answers false, which shows the
  // teaser: the safe direction, since the sentence is true of Free.
  useEffect(() => {
    let cancelled = false;
    void getPlan(tenantId)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch(() => {
        /* the screen is fine without it — see above */
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setFailure(null);
      try {
        const [people, credits, projects, auds] = await Promise.all([
          listAllRecordsByType(tenantId, AA.PERSON),
          listAllRecordsWithLinks(tenantId, AA.CREW_CREDIT, {
            linkTypes: [LINK.CREDIT_PERSON, LINK.CREDIT_PROJECT],
          }),
          listAllRecordsByType(tenantId, AA.PROJECT),
          listAllRecordsWithLinks(tenantId, AA.AUDITION, { linkTypes: [LINK.AUDITION_PEOPLE] }),
        ]);
        if (cancelled) return;

        const ends = creditEnds(activeOnly(credits.records), credits.linksByRecordId);
        const projectById = new Map(activeOnly(projects).map((p) => [p.id, p]));
        const auditions = activeOnly(auds.records);

        const next: Line[] = activeOnly(people).map((person) => {
          const theirs = creditsForPerson(ends, person.id);
          const latest = theirs[0] ?? null;
          return {
            person,
            latest,
            latestProject: latest?.projectId ? projectById.get(latest.projectId) ?? null : null,
            credits: theirs.length,
            reads: readsWithPerson(auditions, auds.linksByRecordId, person.id),
          };
        });

        // The people a household has dealt with MOST, first. A registry sorted
        // by name is a phone book; sorted by how often they have actually been
        // in the room it is the answer to "who do we know here". Ties fall back
        // to the name so the order is stable between reads rather than
        // whatever the database happened to return.
        next.sort(
          (a, b) =>
            b.reads + b.credits - (a.reads + a.credits) ||
            a.person.display_name.localeCompare(b.person.display_name)
        );
        setLines(next);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLines(null);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce, recordsVersion]);

  if (failure) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">People</div>
        <div class="sp__say">
          {failure.headline}
          <br />
          <span class="sp__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="sp__retry" onClick={() => setRetryNonce((n) => n + 1)}>
            Read the people again
          </button>
        ) : null}
      </div>
    );
  }

  if (lines === null) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">People</div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  // ⚑ THE TEASER IS ONE SENTENCE, IT IS TRUE, AND IT APPEARS ONLY WHERE IT IS
  // TRUE. Below Working a call sheet's crew block is not read into anything
  // (the pack gates `aa_crew_credit` on `people.fill_from_callsheets`), so an
  // empty People tab on Free has a reason worth naming. On Working and above
  // the same emptiness means only "nobody yet" and the line would be a lie
  // about a feature the household already has.
  //
  // ⚠ NOT AN INSTRUCTION. It says what the product does on a plan, not what to
  // do next — no "tap +", no "upload a call sheet to get started". The "+" is
  // beside it and teaches itself.
  const fillsItself = planHas(plan, 'people.fill_from_callsheets');
  const positionSearchOn = planHas(plan, 'people.position_roster');

  return (
    <div class="sp">
      <div class="sp__eyebrow">People</div>

      {/* ===================================================================
          P3 — THE POSITION SEARCH (people.position_roster, Pro)
          =================================================================== */}
      {positionSearchOn ? (
        <form
          class="sp__acts"
          style={{ marginBottom: '10px' }}
          onSubmit={(e) => {
            e.preventDefault();
            void runPositionSearch(positionQuery);
          }}
        >
          <input
            type="text"
            class="sp__craft-self"
            style={{ minHeight: 'auto', padding: '6px 10px', flex: '1 1 200px' }}
            data-testid="position-search-input"
            value={positionQuery}
            onInput={(e) => setPositionQuery((e.target as HTMLInputElement).value)}
            placeholder="Who has 1st AD'd for us?"
          />
          <button type="submit" class="sp__act" disabled={positionSearching}>
            {positionSearching ? 'Searching…' : 'Search'}
          </button>
        </form>
      ) : (
        // THE TEASER — one honest line, unconditional (craft.layer's own
        // precedent): the search is gated whole, so there is no box to half
        // offer and no count to condition the sentence on.
        <p class="sp__craft-say" data-testid="position-search-teaser">
          On Pro, search everyone you've worked with by position — "who was 1st AD on my last job?"
        </p>
      )}

      {positionSearchOn && positionSay ? (
        <p class="sp__say-soft" data-testid="position-search-failure">
          {positionSay}
        </p>
      ) : null}

      {positionSearchOn && positionResults ? (
        <div style={{ marginBottom: '14px' }}>
          {positionResults.length === 0 ? (
            <div class="sp__empty">Nobody's position matches "{positionQuery.trim()}".</div>
          ) : (
            positionResults.map((r) => (
              <button
                type="button"
                key={r.creditId}
                class="sp__row"
                data-testid="position-search-result"
                disabled={!r.personId}
                onClick={() => r.personId && onOpen(r.personId)}
              >
                <span class="sp__row-top">
                  <span class="sp__row-name">{r.personName ?? 'Not attributed to anyone yet'}</span>
                </span>
                <span class="sp__row-sub">
                  {r.position}
                  {r.projectName ? ` · ${r.projectName}` : ''}
                </span>
              </button>
            ))
          )}
        </div>
      ) : null}

      {lines.length === 0 ? (
        <>
          <div class="sp__empty">
            No agents, casting offices, coaches or crew recorded yet.
            {fillsItself ? '' : ' Call sheets fill this on the Working plan.'}
          </div>
          <div class="sp__acts">
            <button type="button" class="sp__act" onClick={() => openRecordEditor({ mode: 'create', typeKey: AA.PERSON })}>
              Add someone
            </button>
          </div>
        </>
      ) : (
        <>
          {lines.map((line) => (
            <button type="button" key={line.person.id} class="sp__row" onClick={() => onOpen(line.person.id)}>
              <span class="sp__row-top">
                <span class="sp__row-name">{line.person.display_name}</span>
                {/* The count a family could not have worked out from this
                    screen, and nothing else. A person seen once says nothing
                    here — "1 read" is noise dressed as information, the same
                    call the audition spread's ordinal makes. */}
                <span class="sp__row-when">{standingWords(line)}</span>
              </span>
              <span class="sp__row-sub">{subtitleFor(line)}</span>
            </button>
          ))}
          <div class="sp__acts">
            <button type="button" class="sp__act" onClick={() => openRecordEditor({ mode: 'create', typeKey: AA.PERSON })}>
              Add someone
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** The right-hand mono line: how many of this household's reads this person
 * has been named on, from the SECOND. See the JSX comment above on why one is
 * not reported. */
function standingWords(line: Line): string {
  if (line.reads >= 2) return `${line.reads} reads`;
  return '';
}

/**
 * The line under the name: what they did, most recently, and on what.
 *
 * ⚠ THE POSITION IS RENDERED VERBATIM AND THE PACK ROLE IS NOT. `position` is
 * a text field copied off a call sheet — "1st AD", "Key MUA" — and a formatter
 * would make "1St Ad" of it. `person_role` is a picklist of OUR values
 * (`casting_director`), which is exactly what `humanizeValue` is for. Two
 * different kinds of string, two different treatments, on one line.
 */
function subtitleFor(line: Line): string {
  const position = line.latest ? positionOf(line.latest.credit) : null;
  const role = line.person.fields.person_role;
  const what = position ?? (typeof role === 'string' && role ? humanizeValue(role) : null);
  const where = line.latestProject?.display_name ?? null;
  const more =
    line.credits >= 2 ? `${line.credits} credits` : null;

  const parts = [
    what && where ? `${what} · ${where}` : what ?? where,
    more,
    // A person with a company but nothing else to say is still worth placing:
    // "Whitfield Casting" tells a parent who they are looking at.
    !what && !where && typeof line.person.fields.company === 'string' && line.person.fields.company
      ? line.person.fields.company
      : null,
  ].filter((s): s is string => !!s);

  return parts.length > 0 ? parts.join(' · ') : '—';
}
